import { Link } from "react-router-dom";
import type { Work } from "@/types/content";
import { categoryName, coverOf, formatViews } from "@/lib/content";

interface WorkCardProps {
  work: Work;
  rank?: number;
  compact?: boolean;
}

export default function WorkCard({ work, rank, compact = false }: WorkCardProps) {
  return (
    <Link
      to={`/detail/${work.id}`}
      className="group block cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 rounded-lg"
    >
      <div className="relative w-full aspect-[3/4] overflow-hidden rounded-lg bg-background-200">
        <img
          src={coverOf(work)}
          alt={`${work.title} 封面`}
          title={`${work.title} · ${categoryName(work.category)} · ${work.year} 高清成人影视资料`}
          className="h-full w-full object-cover object-top transition duration-700 ease-smooth group-hover:scale-[1.06]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/95 via-background-950/10 to-transparent" />

        <span className="absolute left-2 top-2 rounded-md bg-background-950/70 px-2 py-1 text-[11px] font-medium tracking-wide text-primary-300 backdrop-blur-sm">
          {categoryName(work.category)}
        </span>

        <span className="absolute right-2 top-2 flex items-center gap-1 rounded-md bg-background-950/70 px-2 py-1 text-[11px] font-semibold text-primary-300 backdrop-blur-sm">
          <i className="ri-star-fill text-[10px]" />
          {work.rating.toFixed(1)}
        </span>

        {rank ? (
          <span className="absolute bottom-2 left-2 z-10 flex h-7 min-w-7 items-center justify-center rounded-md bg-primary-500 px-1.5 text-sm font-bold text-background-950">
            {String(rank).padStart(2, "0")}
          </span>
        ) : null}

        <div className="absolute bottom-0 left-0 right-0 translate-y-2 px-2.5 pb-2.5 opacity-0 transition duration-300 group-hover:translate-y-0 group-hover:opacity-100">
          <span className="flex items-center justify-center gap-1 rounded-md bg-primary-500 py-1.5 text-[11px] font-semibold text-background-950">
            <i className="ri-information-line" />
            查看资料
          </span>
        </div>
      </div>

      <div className="mt-2.5">
        <h3 className="truncate text-[13px] font-medium text-foreground-100 transition group-hover:text-primary-400 md:text-sm">
          {work.title}
        </h3>
        {!compact ? (
          <p className="mt-1 flex items-center gap-1.5 truncate text-[11px] text-foreground-500">
            <span>{work.year}</span>
            <span className="text-foreground-700">·</span>
            <span>{work.region}</span>
            <span className="text-foreground-700">·</span>
            <span>{work.episodeCount} 话</span>
          </p>
        ) : null}
        {compact ? (
          <p className="mt-1 truncate text-[11px] text-foreground-500">
            {work.region} · {work.episodeCount} 话 · {formatViews(work.views)} 浏览
          </p>
        ) : null}
      </div>
    </Link>
  );
}